import React from "react";
import { ExpandableHeader } from "../../../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import ScrollbarWrapper from "../../../../../components/ACS-Dashboard/ACS-Blocks/ScrollbarWrapper";
import {
  Activity,
  Clock3,
  GitCompare,
  MessageSquare,
  User,
  X,
} from "lucide-react";

const normalizeUpdatedBy = (updatedBy) => {
  if (typeof updatedBy === "string" && updatedBy.trim().toUpperCase().startsWith("EDI")) {
    return "EDI_SYSTEM";
  }
  return updatedBy;
};

const parseActivityValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      /* ignore JSON parse errors */
    }
    return trimmed;
  }
  return value;
};

const renderValue = (val, formatDisplayDate) => {
  const parsed = parseActivityValue(val);

  const formatIfDateLike = (value) => {
    if (!formatDisplayDate) return value;
    if (value instanceof Date) {
      return formatDisplayDate(value.toISOString());
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed)) {
        const formatted = formatDisplayDate(trimmed);
        if (formatted) return formatted;
      }
    }
    return value;
  };

  const formatted = formatIfDateLike(parsed);

  if (formatted === null || formatted === undefined) {
    return (
      <span className="text-gray-400 dark:text-acs-dark-subtext">—</span>
    );
  }

  if (Array.isArray(formatted)) {
    return (
      <ul className="space-y-1 text-xs">
        {formatted.map((entry, idx) => (
          <li
            key={idx}
            className="rounded bg-gray-100 px-2 py-1 text-gray-700 dark:bg-acs-dark-border dark:text-acs-dark-text"
          >
            {String(formatIfDateLike(entry))}
          </li>
        ))}
      </ul>
    );
  }

  if (typeof formatted === "object") {
    return (
      <dl className="space-y-1 text-xs">
        {Object.entries(formatted).map(([key, v]) => (
          <div
            key={key}
            className="flex items-start justify-between gap-2 leading-tight"
          >
            <dt className="text-gray-500 dark:text-acs-dark-subtext">
              {key}
            </dt>
            <dd className="flex-1 text-right text-gray-800 dark:text-acs-dark-text">
              {String(formatIfDateLike(v) ?? "-")}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <span className="text-gray-800 dark:text-acs-dark-text break-words">
      {String(formatted)}
    </span>
  );
};

// val = num -> format to 2 decimals
const formatValue = (val) => {
  if (val === null || val === undefined) return val;
  const num = typeof val === "number" ? val : Number(val);

  if (!isNaN(num)) {
    return num.toFixed(2);
  }

  return val; // return orig if not numeric
};

const ValueCard = ({ label, value, formatDisplayDate }) => (
  <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-fluid-2 shadow-sm dark:border-acs-dark-border dark:from-acs-dark-surface dark:to-acs-dark-bg">
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-acs-dark-subtext">
      {label}
    </p>
    <div className="mt-1 text-fluid-sm">
      {renderValue(value, formatDisplayDate)}
    </div>
  </div>
);

// Match shipment table status colors
const statusBadgeClass = {
  NEW: "bg-blue-100 dark:bg-blue-900/40 text-black dark:text-blue-200",
  ASSIGNED: "bg-amber-100 dark:bg-amber-900/40 text-black dark:text-amber-200",
  UNASSIGNED: "bg-neutral-200 dark:bg-neutral-700/40 text-black dark:text-gray-200",
  ACCEPTED: "bg-emerald-100 dark:bg-emerald-900/40 text-black dark:text-emerald-200",
  TENDERED: "bg-indigo-100 dark:bg-indigo-900/40 text-black dark:text-indigo-200",
  "READY FOR PICKUP": "bg-cyan-100 dark:bg-cyan-900/40 text-black dark:text-cyan-200",
  ACTIVE: "bg-teal-100 dark:bg-teal-900/40 text-black dark:text-teal-200",
  COMPLETED: "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200",
  INVOICED: "bg-purple-100 dark:bg-purple-900/40 text-black dark:text-purple-200",
  CANCELLED: "bg-red-100 dark:bg-red-900/40 text-black dark:text-red-200",
  DEFAULT: "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
};

const statusDisplayLabel = {
  ACTIVE: "In Transit",
  COMPLETED: "Delivered",
};

const getStatusClasses = (status) => {
  if (!status) return statusBadgeClass.DEFAULT;
  const normalized = status.toString().trim().toUpperCase();
  return statusBadgeClass[normalized] || statusBadgeClass.DEFAULT;
};

const getStatusLabel = (status) => {
  if (!status) return "Update";
  const normalized = status.toString().trim().toUpperCase();
  return statusDisplayLabel[normalized] || status;
};

const SkeletonLine = ({ width = "w-full" }) => (
  <div
    className={`h-3 animate-pulse rounded bg-gray-200 dark:bg-acs-dark-border ${width}`}
  />
);

const PreviewSkeleton = () => (
  <div className="space-y-fluid-2">
    {Array.from({ length: 3 }).map((_, idx) => (
      <div
        key={idx}
        className="rounded-lg border border-gray-200 bg-white p-fluid-2 shadow-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
      >
        <SkeletonLine width="w-1/2" />
        <div className="mt-2 space-y-1">
          <SkeletonLine />
          <SkeletonLine width="w-5/6" />
        </div>
      </div>
    ))}
  </div>
);

const DrawerSkeleton = () => (
  <div className="space-y-fluid-2">
    {Array.from({ length: 4 }).map((_, idx) => (
      <div
        key={idx}
        className="rounded-xl border border-gray-200 bg-white p-fluid-3 shadow-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
      >
        <div className="space-y-2">
          <SkeletonLine width="w-2/3" />
          <SkeletonLine />
          <SkeletonLine width="w-11/12" />
        </div>
      </div>
    ))}
  </div>
);

const ActivityDrawer = ({
  isOpen,
  onClose,
  logs,
  formatDisplayDate,
  isActivityLoading,
}) => {
  const hasLogs = logs.length > 0;
  return (
    <div
      className={`fixed inset-0 z-40 ${isOpen ? "" : "pointer-events-none"}`}
    >
      <div
        className={`absolute inset-0 bg-slate-900/30 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-[75vw] transform transition-transform duration-300 ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex h-full flex-col overflow-hidden bg-white shadow-2xl ring-1 ring-black/5 dark:bg-acs-dark-surface dark:ring-acs-dark-border">
          <header className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gradient-to-r from-acs-primary-navy to-acs-primary-blue px-fluid-3 py-fluid-2 text-white dark:border-acs-dark-border">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10">
                <Activity size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-white/70">
                  Shipment Timeline
                </p>
                <p className="text-fluid-lg font-semibold">Activity Log</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60"
            >
              <X size={18} />
            </button>
          </header>

          <ScrollbarWrapper className="flex-1 overflow-y-auto bg-gray-50/60 p-fluid-3 font-roboto text-fluid-base dark:bg-acs-dark-bg">
            {isActivityLoading ? (
              <DrawerSkeleton />
            ) : hasLogs ? (
              <ol className="space-y-fluid-1.5">
                {logs.map((item, idx) => {
                  const formattedDate = formatDisplayDate
                    ? formatDisplayDate(item.updatedTime)
                    : item.updatedTime || "—";
                  const isLast = idx === logs.length - 1;
                  return (
                    <li key={`${item.updatedTime}-${idx}`} className="relative pl-9">
                      {!isLast && (
                        <span className="absolute left-4 top-6 h-full w-px bg-gradient-to-b from-acs-secondary-mint/70 via-acs-primary-blue/30 to-transparent" />
                      )}
                      <span className="absolute left-1.5 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-white ring-4 ring-acs-secondary-mint shadow-sm dark:bg-acs-dark-surface dark:ring-acs-primary-blue/40">
                        <span className="h-2 w-2 rounded-full bg-acs-primary-blue" />
                      </span>

                      <div className="rounded-xl border border-gray-200 bg-white p-fluid-1.5 shadow-sm dark:border-acs-dark-border dark:bg-acs-dark-surface">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusClasses(
                                item.status
                              )}`}
                            >
                              <Activity size={14} />
                              {getStatusLabel(item.status)}
                            </span>
                            {item.fieldName ? (
                              <span className="rounded-full bg-acs-secondary-mint/70 px-2 py-1 text-xs text-acs-primary-navy dark:bg-acs-primary-blue/25 dark:text-acs-dark-text">
                                {item.fieldName}
                              </span>
                            ) : null}
                          </div>
                          <span className="inline-flex items-center gap-1 text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">
                            <Clock3 size={14} />
                            {formattedDate}
                          </span>
                        </div>

                        <div className="mt-1 flex flex-wrap items-center gap-2 text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">
                          <User size={14} />
                          <span className="font-medium text-gray-800 dark:text-acs-dark-text">
                            {normalizeUpdatedBy(item.updatedBy) || "System"}
                          </span>
                          <span className="text-gray-400 dark:text-acs-dark-subtext">
                            •
                          </span>
                          <span>Shipment #{item.shipmentNumber || "—"}</span>
                        </div>

                        <div className="mt-fluid-1.5 grid grid-cols-1 gap-fluid-1.5 md:grid-cols-2">
                          <ValueCard
                            label="Previous"
                            value={formatValue(item.prevValue)}
                            formatDisplayDate={formatDisplayDate}
                          />
                          <ValueCard
                            label="New"
                            value={formatValue(item.newValue)}
                            formatDisplayDate={formatDisplayDate}
                          />
                        </div>

                        {item.reason ? (
                          <div className="mt-fluid-1.5 inline-flex items-start gap-2 rounded-lg bg-acs-secondary-mint/80 px-3 py-2 text-fluid-sm text-acs-primary-navy dark:bg-acs-primary-blue/25 dark:text-acs-dark-text">
                            <MessageSquare size={14} />
                            <span className="leading-snug">{item.reason}</span>
                          </div>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white/70 p-fluid-4 text-fluid-sm text-gray-600 dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-subtext">
                No activity logs yet. Updates will appear here as a clean
                timeline.
              </div>
            )}
          </ScrollbarWrapper>
        </div>
      </aside>
    </div>
  );
};

const ShipmentActivity = ({
  setSectionRef,
  isActivityLoading,
  activityLogs,
  formatDisplayDate,
  isOpen,
  onOpen,
  onClose,
  showInline = true,
}) => {
  const safeLogs = Array.isArray(activityLogs) ? activityLogs : [];
  const previewLogs = safeLogs.slice(0, 3);
  const hasActivity = safeLogs.length > 0;

  const openDrawer = () => onOpen?.();
  const closeDrawer = () => onClose?.();

  return (
    <>
      {showInline ? (
        <div className="mt-fluid-4 font-roboto text-fluid-base">
          <div className="mb-fluid-2 flex items-center justify-between">
            <h3
              ref={setSectionRef("Shipment Activity")}
              className="text-fluid-lg font-semibold"
            >
              Shipment Activity
            </h3>
            <span className="text-xs text-gray-500 dark:text-acs-dark-subtext">
              Slides in from the right for the full story.
            </span>
          </div>

          <ExpandableHeader
            headerLabel="Activity Timeline"
            icon={Activity}
            headerType="primary"
            initiallyExpanded
          >
            <div className="space-y-fluid-3 rounded-xl border border-gray-200 bg-white p-fluid-3 shadow-sm dark:border-acs-dark-border dark:bg-acs-dark-surface">
              <div className="flex flex-wrap items-start justify-between gap-fluid-2">
                <div>
                  <p className="text-fluid-base font-semibold text-gray-900 dark:text-acs-dark-text">
                    Timeline preview
                  </p>
                  <p className="text-sm text-gray-600 dark:text-acs-dark-subtext">
                    Open the slide-in panel to explore every change on this
                    shipment.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-acs-secondary-mint px-3 py-1 text-xs font-semibold text-acs-primary-navy dark:bg-acs-primary-blue/25 dark:text-acs-dark-text">
                    {safeLogs.length} updates
                  </span>
                  <button
                    type="button"
                    onClick={openDrawer}
                    className="inline-flex items-center gap-2 rounded-md bg-acs-primary-navy px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-acs-primary-blue focus:outline-none focus:ring-2 focus:ring-acs-accent focus:ring-offset-2"
                  >
                    <GitCompare size={16} />
                    Open timeline
                  </button>
                </div>
              </div>

              {isActivityLoading ? (
                <PreviewSkeleton />
              ) : hasActivity ? (
                <div className="space-y-fluid-2">
                  {previewLogs.map((item, idx) => {
                    const formattedDate = formatDisplayDate
                      ? formatDisplayDate(item.updatedTime)
                      : item.updatedTime || "—";
                    return (
                      <div
                        key={`${item.updatedTime}-${idx}`}
                        className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-fluid-2 shadow-sm dark:border-acs-dark-border dark:bg-acs-dark-bg"
                      >
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-acs-secondary-mint text-acs-primary-navy font-semibold">
                          {idx + 1}
                        </div>
                        <div className="flex-1 space-y-1">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${getStatusClasses(
                                  item.status
                                )}`}
                              >
                                <Activity size={14} />
                                {getStatusLabel(item.status)}
                              </span>
                              {item.fieldName ? (
                                <span className="rounded-full bg-white/80 px-2 py-1 text-xs text-gray-700 dark:bg-acs-dark-surface dark:text-acs-dark-text">
                                  {item.fieldName}
                                </span>
                              ) : null}
                            </div>
                            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-xs text-gray-600 shadow-sm dark:bg-acs-dark-surface dark:text-acs-dark-subtext">
                              <Clock3 size={14} />
                              {formattedDate}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-acs-dark-subtext">
                            {normalizeUpdatedBy(item.updatedBy) || "System"} • Shipment #
                            {item.shipmentNumber || "—"}
                          </p>
                          {item.reason ? (
                            <p className="text-xs text-gray-700 dark:text-acs-dark-text">
                              {item.reason}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-fluid-3 text-sm text-gray-600 dark:border-acs-dark-border dark:bg-acs-dark-bg dark:text-acs-dark-subtext">
                  No activity logs yet. Use the Activity button near Print to
                  slide in the full timeline when updates arrive.
                </div>
              )}
            </div>
          </ExpandableHeader> 
        </div>
      ) : null}

      <ActivityDrawer
        isOpen={isOpen}
        onClose={closeDrawer}
        logs={safeLogs}
        formatDisplayDate={formatDisplayDate}
        isActivityLoading={isActivityLoading}
      />
    </>
  );
};

export default ShipmentActivity;